/// SI decimal, matching how the plan catalog states its numbers (2 GB is
/// 2_000_000_000, not 2^31) and how storage_monitoring's own
/// `*_human_readable` strings are built. Binary units here would print a
/// different figure from the one the pricing page sold.
export function formatBytes(bytes: number): string {
  const units = [
    { limit: 1_000_000_000_000, suffix: "TB", div: 1_000_000_000_000 },
    { limit: 1_000_000_000, suffix: "GB", div: 1_000_000_000 },
    { limit: 1_000_000, suffix: "MB", div: 1_000_000 },
    { limit: 1_000, suffix: "KB", div: 1_000 },
  ];
  for (const { limit, suffix, div } of units) {
    if (bytes >= limit) {
      const value = bytes / div;
      // One decimal below 10 so "1.2 GB" doesn't collapse to "1 GB", none above
      // it where the digit is noise against the magnitude.
      return `${value < 10 ? value.toFixed(1) : Math.round(value).toLocaleString()} ${suffix}`;
    }
  }
  return `${Math.round(bytes).toLocaleString()} B`;
}

/// Money arrives in the lowest currency unit; these have no minor digits.
const ZERO_DECIMAL_CURRENCIES = new Set(["JPY", "KRW", "CLP"]);

function fromLowestUnit(amount: number, currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency) ? amount : amount / 100;
}

/// Viewer-locale currency formatting. Never round on top of it.
function money(value: number, currency: string): string {
  return new Intl.NumberFormat(navigator.language || "en-US", {
    style: "currency",
    currency,
  }).format(value);
}

/// The caller states the period.
export function formatMoneyCents(priceCents: number, currency: string): string {
  return money(fromLowestUnit(priceCents, currency), currency);
}

/// A processor amount, which Paddle sends as a string.
export function formatProcessorMoney(amount: string, currency: string): string {
  const parsed = parseFloat(amount);
  if (!Number.isFinite(parsed)) return `${amount} ${currency}`;
  return money(fromLowestUnit(parsed, currency), currency);
}
